import * as React from 'react';
import { ListPageLayout } from './list-page-layout';
import { buildEmptyFilters, countActiveFilters, isApiBackedProps } from './list-page.helpers';
import type {
	IListPageBaseProps,
	IListPageFilterConfig,
	IListPageProps,
	IListPageStrings,
} from './list-page.types';
import { useListPageData, type IListPageDataPageInput, type IListPageDataResult } from './use-list-page-data';
import { useListPageActionsHandle } from './use-list-page-actions-handle';
import { useListPageInputState } from './use-list-page-input-state';
import { useIntersectionObserverRef } from '../../../hooks/use-intersection-observer-ref';
import { Filter, Input } from '@components/atoms';
import { Body2Typography } from '@components/atoms/typography';
import { Button, ButtonSize, ButtonVariant } from '@components/molecules';
import { LabeledCheckbox } from '@components/molecules/labeled-checkbox/labeled-checkbox';
import { RadioGroup } from '@components/molecules/radio/radio-group/radio-group';

const INTERSECTION_ROOT_MARGIN = '100px';

interface IListPageFiltersPanelProps {
	configs: IListPageFilterConfig[];
	values: Record<string, unknown>;
	onChange: (paramName: string, value: unknown) => void;
}

const ListPageFiltersPanel: React.FC<IListPageFiltersPanelProps> = ({ configs, values, onChange }) => (
	<div className="flex flex-col gap-4" data-testid="list-page-filters-panel">
		{configs.map(config => {
			const filterType = config.type ?? 'multi';

			if (filterType === 'single' || filterType === 'date-range') {
				const currentValue = (values[config.paramName] as string | undefined) ?? '';
				return (
					<div key={config.paramName} className="flex flex-col gap-2">
						<Body2Typography className="text-primary font-medium" truncate={false}>
							{config.label}
						</Body2Typography>
						<RadioGroup
							value={currentValue}
							onValueChange={value => onChange(config.paramName, value)}
							options={config.options.map(option => ({ label: option.label, value: option.value }))}
						/>
					</div>
				);
			}

			const selected = ((values[config.paramName] as string[] | undefined) ?? []) as string[];
			const handleToggle = (optionValue: string) => {
				const next = selected.includes(optionValue)
					? selected.filter(v => v !== optionValue)
					: [...selected, optionValue];
				onChange(config.paramName, next);
			};

			return (
				<div key={config.paramName} className="flex flex-col gap-2">
					<Body2Typography className="text-primary font-medium" truncate={false}>
						{config.label}
					</Body2Typography>
					<div className="flex flex-col gap-1">
						{config.options.map(option => (
							<LabeledCheckbox
								key={option.value}
								id={`${config.paramName}-${option.value}`}
								label={option.label}
								checked={selected.includes(option.value)}
								onCheckedChange={() => handleToggle(option.value)}
							/>
						))}
					</div>
				</div>
			);
		})}
	</div>
);
ListPageFiltersPanel.displayName = 'ListPageFiltersPanel';

interface IComposeLabelsInput {
	strings?: IListPageStrings;
	hasError: boolean;
	hasItems: boolean;
}

const composeListLabels = ({ strings, hasError, hasItems }: IComposeLabelsInput) => {
	if (!strings) return undefined;
	return {
		empty: strings.empty,
		error: hasError ? strings.error : undefined,
		endOfPage: hasItems ? strings.endOfPage : undefined,
	};
};

interface IListPageRendererProps<TRow> extends Omit<IListPageBaseProps<TRow>, 'enabled' | 'itemsPerPage'> {
	dataResult: IListPageDataResult<TRow>;
	searchValue: string;
	onSearchChange: (next: string) => void;
	appliedFilters: Record<string, unknown>;
	onApplyFilters: (next: Record<string, unknown>) => void;
}

function ListPageRenderer<TRow>({
	title,
	subtitle,
	banner,
	renderItem,
	itemKey,
	search: searchConfig,
	filters: filterConfigs,
	extras,
	strings,
	isMobile = false,
	className,
	pageSkeleton,
	dataResult,
	searchValue,
	onSearchChange,
	appliedFilters,
	onApplyFilters,
}: IListPageRendererProps<TRow>): JSX.Element {
	const { items, isLoading, isFetching, error, hasMore, loadMore } = dataResult;
	const triggerWrapperRef = React.useRef<HTMLDivElement>(null);

	const sentinelRef = useIntersectionObserverRef({
		onIntersect: loadMore,
		enabled: hasMore,
		rootMargin: INTERSECTION_ROOT_MARGIN,
	});

	const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
	const configs = React.useMemo(() => filterConfigs ?? [], [filterConfigs]);
	const [draftFilters, setDraftFilters] = React.useState<Record<string, unknown>>(() => ({
		...buildEmptyFilters(configs),
		...appliedFilters,
	}));

	const wasFiltersOpenRef = React.useRef(false);
	React.useEffect(() => {
		if (isFiltersOpen && !wasFiltersOpenRef.current) {
			setDraftFilters({ ...buildEmptyFilters(configs), ...appliedFilters });
		}
		wasFiltersOpenRef.current = isFiltersOpen;
	}, [isFiltersOpen, configs, appliedFilters]);

	const activeFilterCount = countActiveFilters(configs, appliedFilters);
	// hasMore guards against a transient "empty" flash when consumers filter fetched pages
	// client-side and a page's rows are all filtered out while later pages are still loading.
	const isEmpty = !isLoading && !isFetching && !error && items.length === 0 && !hasMore;
	const showSentinel = hasMore && !isLoading;

	const labels = composeListLabels({
		strings,
		hasError: Boolean(error),
		hasItems: items.length > 0,
	});

	const onApply = () => {
		setIsFiltersOpen(false);
		onApplyFilters(draftFilters);
	};

	const onClear = () => {
		const cleared = buildEmptyFilters(configs);
		setDraftFilters(cleared);
		onApplyFilters(cleared);
	};

	const hasControls = Boolean(searchConfig || configs.length || extras);

	const isInitialLoading = isLoading && !error && items.length === 0;
	if (isInitialLoading && pageSkeleton) {
		return <>{pageSkeleton}</>;
	}

	return (
		<ListPageLayout className={className}>
			<ListPageLayout.Header title={title} subtitle={subtitle} banner={banner} />

			{hasControls && (
				<ListPageLayout.Controls>
					{searchConfig && (
						<ListPageLayout.Search>
							<Input
								variant="search"
								placeholder={searchConfig.placeholder}
								value={searchValue}
								onChange={event => onSearchChange(event.target.value)}
								data-testid="list-page-search-input"
							/>
						</ListPageLayout.Search>
					)}

					{configs.length > 0 && (
						<>
							<ListPageLayout.FilterTrigger activeCount={activeFilterCount}>
								<div ref={triggerWrapperRef}>
									<Button
										variant={ButtonVariant.ICON}
										size={ButtonSize.SM}
										className="h-auto p-[6px]"
										aria-label={strings?.filterTrigger ?? 'Filters'}
										onClick={() => setIsFiltersOpen(prev => !prev)}
										data-testid="list-page-filter-button"
									>
										<Filter className="h-4 w-4 text-primary" aria-hidden />
									</Button>
								</div>
							</ListPageLayout.FilterTrigger>
							<ListPageLayout.FilterDialog
								open={isFiltersOpen}
								onOpenChange={setIsFiltersOpen}
								anchorRef={triggerWrapperRef}
								isMobile={isMobile}
								footer={
									<div className="flex w-full justify-end gap-2">
										<Button
											variant={ButtonVariant.SECONDARY}
											size={ButtonSize.SM}
											onClick={onClear}
											data-testid="list-page-filter-clear"
										>
											{strings?.filterClear ?? 'Clear'}
										</Button>
										<Button
											variant={ButtonVariant.PRIMARY}
											size={ButtonSize.SM}
											onClick={onApply}
											data-testid="list-page-filter-apply"
										>
											{strings?.filterApply ?? 'Apply'}
										</Button>
									</div>
								}
							>
								<ListPageFiltersPanel
									configs={configs}
									values={draftFilters}
									onChange={(paramName, value) =>
										setDraftFilters(prev => ({ ...prev, [paramName]: value }))
									}
								/>
							</ListPageLayout.FilterDialog>
						</>
					)}

					{extras && <ListPageLayout.Extras>{extras}</ListPageLayout.Extras>}
				</ListPageLayout.Controls>
			)}

			<ListPageLayout.List
				isLoading={isLoading}
				isEmpty={isEmpty}
				error={error}
				hasMore={hasMore}
				labels={labels}
			>
				{items.map((item, index) => {
					const key = itemKey ? itemKey(item, index) : index;
					return <React.Fragment key={key}>{renderItem(item, index)}</React.Fragment>;
				})}
				{showSentinel && <ListPageLayout.LoadMoreSentinel ref={sentinelRef} />}
			</ListPageLayout.List>
		</ListPageLayout>
	);
}

type ApiBackedProps<TItem, TRow> = Extract<IListPageProps<TItem, TRow>, { apiMethod: unknown }>;
type HookBackedProps<TItem, TRow> = Extract<IListPageProps<TItem, TRow>, { useData: unknown }>;

function ListPageWithApi<TItem, TRow>({
	apiMethod,
	apiResponseParser,
	queryKey,
	staleTime,
	cacheTime,
	itemsPerPage,
	enabled,
	...rest
}: ApiBackedProps<TItem, TRow>): JSX.Element {
	const { search, setSearch, appliedFilters, setAppliedFilters, resolveFilters } = useListPageInputState({
		filterConfigs: rest.filters,
		searchDebounceMs: rest.search?.debounceMs,
	});

	const wrappedApiMethod = React.useCallback(
		(input: IListPageDataPageInput, options?: { signal?: AbortSignal }) =>
			apiMethod({ ...input, filters: resolveFilters(input.filters) }, options),
		[apiMethod, resolveFilters]
	);

	const dataResult = useListPageData<TItem, TRow>({
		apiMethod: wrappedApiMethod,
		apiResponseParser,
		queryKey,
		search,
		filters: appliedFilters,
		itemsPerPage,
		enabled,
		staleTime,
		cacheTime,
		searchDebounceMs: rest.search?.debounceMs,
	});

	useListPageActionsHandle({ refetch: dataResult.refetch }, rest.actionsRef);

	const applyFilters = (next: Record<string, unknown>) => {
		setAppliedFilters(next);
		rest.onFiltersChange?.(next);
	};

	return (
		<ListPageRenderer<TRow>
			{...rest}
			dataResult={dataResult}
			searchValue={search}
			onSearchChange={setSearch}
			appliedFilters={appliedFilters}
			onApplyFilters={applyFilters}
		/>
	);
}

function ListPageWithHook<TItem, TRow>({ useData, ...rest }: HookBackedProps<TItem, TRow>): JSX.Element {
	const {
		search,
		setSearch,
		debouncedSearch,
		appliedFilters,
		setAppliedFilters,
		resolvedFilters,
	} = useListPageInputState({
		filterConfigs: rest.filters,
		searchDebounceMs: rest.search?.debounceMs,
	});

	const dataResult = useData({ search: debouncedSearch, filters: resolvedFilters });

	useListPageActionsHandle({ refetch: dataResult.refetch }, rest.actionsRef);

	const applyFilters = (next: Record<string, unknown>) => {
		setAppliedFilters(next);
		rest.onFiltersChange?.(next);
	};

	return (
		<ListPageRenderer<TRow>
			{...rest}
			dataResult={dataResult}
			searchValue={search}
			onSearchChange={setSearch}
			appliedFilters={appliedFilters}
			onApplyFilters={applyFilters}
		/>
	);
}

export function ListPage<TItem, TRow = TItem>(props: IListPageProps<TItem, TRow>): JSX.Element {
	if (isApiBackedProps(props)) {
		return <ListPageWithApi<TItem, TRow> {...props} />;
	}
	return <ListPageWithHook<TItem, TRow> {...(props as HookBackedProps<TItem, TRow>)} />;
}
ListPage.displayName = 'ListPage';
